// How long a message takes to get to the server and back, for the `f` readout.
//
// The server already pings every client (ClientProxy.ping) and times the answer, but that number
// lives on the server -- it is what /admin shows -- and it is taken every five seconds, which is too
// slow for a readout meant to show the connection as it is now. So while the readout is up, the
// client times round trips of its own: it sends type_clientPing carrying an id, the server echoes the
// id straight back as type_clientPong, and the gap on this client's clock is the ping.
//
// One probe is in flight at a time, and the next goes out a second after the last one was sent: one
// tiny message a second while somebody is looking, nothing at all otherwise. Holding to one also means
// a stall is never hidden behind the last good answer. A connection that has stopped answering has a
// ping of AT LEAST however long the probe in flight has been waiting, so that is what is reported --
// the reading climbs, rather than sitting on a healthy number from before the connection went quiet.
//
// Replies are matched by id, because not every echo that reaches a client is its own: a session
// recording hands everything the server sent the watched player, pongs included, to an /admin viewer.

/** How often a probe is sent while somebody is looking. */
export const PING_INTERVAL_MS = 1000

// Nobody has asked for the ping for this long -- the readout was put away, or the tab was in the
// background and drew no frames. What was measured back then is history, not the ping now, and a
// probe still unanswered from then would report the whole gap as waiting.
const IDLE_FORGET_MS = 2000

export class PingProbe {
    private inFlight?: { id: string; sentAt: number }
    private lastSentAt = -Infinity
    private lastAskedAt = -Infinity
    private roundTripMs?: number

    /**
     * @param now this client's clock, in ms. Monotonic is best (performance.now): a wall clock
     *   stepped by NTP mid-probe would hand back a round trip that never happened.
     * @param send put a probe on the wire. False when it could not be sent (no open socket), so a
     *   probe that never left is not then waited on.
     * @param nextId a fresh id per probe.
     */
    constructor(
        private readonly now: () => number,
        private readonly send: (id: string) => boolean,
        private readonly nextId: () => string,
    ) {}

    /** Send the next probe if one is due. Cheap enough to call every frame. */
    tick = () => {
        const now = this.now()
        if (now - this.lastAskedAt > IDLE_FORGET_MS) {
            this.forget()
        }
        this.lastAskedAt = now
        if (this.inFlight || now - this.lastSentAt < PING_INTERVAL_MS) {
            return
        }
        const id = this.nextId()
        if (!this.send(id)) {
            return
        }
        this.inFlight = { id, sentAt: now }
        this.lastSentAt = now
    }

    /** An echo arrived. False when it answers no probe this client is waiting on, which is ignored. */
    receive = (id: unknown): boolean => {
        if (!this.inFlight || id !== this.inFlight.id) {
            return false
        }
        this.roundTripMs = Math.max(0, this.now() - this.inFlight.sentAt)
        this.inFlight = undefined
        return true
    }

    /**
     * The ping, in ms: the last round trip, or the wait on the probe in flight once that has run
     * longer. Undefined until there is something to say -- before the first answer, a probe that has
     * only been out for a few ms says nothing about the connection.
     */
    reading = (): number | undefined => {
        const waiting = this.inFlight ? this.now() - this.inFlight.sentAt : undefined
        if (waiting !== undefined && waiting > (this.roundTripMs ?? PING_INTERVAL_MS)) {
            return waiting
        }
        return this.roundTripMs
    }

    /** Drop everything: the socket was replaced, or what was measured is too old to mean anything. */
    forget = () => {
        this.inFlight = undefined
        this.lastSentAt = -Infinity
        this.roundTripMs = undefined
    }
}
